import React, { useState } from 'react';
import { 
  X, Play, CheckCircle2, ArrowRight, ArrowLeft, Sparkles, 
  FileText, ShieldCheck, Download, Layers, Search, Cpu
} from 'lucide-react';
import { Language } from '../utils/i18n';

interface QuickDemoTourModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
}

export default function QuickDemoTourModal({
  isOpen,
  onClose,
  language
}: QuickDemoTourModalProps) {
  const [currentStep, setCurrentStep] = useState(0);

  if (!isOpen) return null;

  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isDe = language === 'de';
  const isFr = language === 'fr';
  const isEn = language === 'en' || (!isUk && !isPl && !isDe && !isFr);

  const steps = [
    {
      stepNumber: 1,
      badge: isUk ? "КРОК 1: ПІДБІР АГЕНТА" : isPl ? "KROK 1: WYBÓR AGENTA" : isDe ? "SCHRITT 1: AGENTEN-AUSWAHL" : isFr ? "ÉTAPE 1: CHOIX DU MODULE" : "STEP 1: AGENT SELECTION",
      title: isUk 
        ? "Опишіть задачу своїми словами або оберіть норматив" 
        : isPl 
        ? "Opisz zadanie własnymi słowami lub wybierz normę" 
        : isDe
        ? "Beschreiben Sie Ihre Aufgabe oder wählen Sie die Branchennorm"
        : isFr
        ? "Décrivez votre besoin ou choisissez la norme applicable"
        : "Describe your task or pick an industry standard",
      desc: isUk
        ? "Smart-Диспетчер миттєво підбирає профільний модуль із бази 301 ШІ-агента (ДСТУ 4163, ПКУ, РЕКН КНУ, KSeF FA_VAT, CBAM, FIDIC)."
        : isPl
        ? "Inteligentny Dyspozytor natychmiast dopasuje moduł spośród 301 agentów AI (Kodeks cywilny, KSeF FA_VAT, KNR, ZUS A1, CBAM, FIDIC)."
        : isDe
        ? "Der Smart-Dispatcher ermittelt sofort das passende Fachmodul aus 301 KI-Agenten (BGB/HGB, XRechnung, DIN 276, VOB/B, CBAM, FIDIC)."
        : isFr
        ? "Le Smart Dispatcher sélectionne instantanément le module adapté parmi 301 agents IA (Code civil, Factur-X, CCAG, CBAM, FIDIC)."
        : "Smart Dispatcher instantly matches your request with one of 301 dedicated AI agents (DSTU, Tax Code, KSeF, CBAM, FIDIC, Eurocodes).",
      icon: Search,
      mockVisual: (
        <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-2 font-mono text-xs text-left">
          <div className="text-[10px] text-slate-400 flex items-center justify-between border-b border-slate-800 pb-1.5">
            <span>{isDe ? '🔍 Suchanfrage des Nutzers:' : isPl ? '🔍 Zapytanie użytkownika:' : isUk ? '🔍 Пошуковий запит користувача:' : '🔍 User search query:'}</span>
            <span className="text-emerald-400 font-bold">{isDe ? '100% Treffer' : isPl ? '100% Zgodność' : isUk ? '100% Збіг' : '100% Match'}</span>
          </div>
          <div className="p-2 bg-slate-900 rounded border border-emerald-500/40 text-emerald-300 font-bold text-[11px]">
            &gt; {isDe ? '«Prüfung Gewerbemietvertrag nach BGB und Klauselprüfung Vertragsstrafen»' : isPl ? '«Audyt umowy najmu lokalu B2B wg Kodeksu cywilnego i kary umowne»' : isUk ? '«Аудит договору оренди за ДСТУ 4163:2020 та перевірка штрафних санкцій»' : '«Commercial Lease Contract Audit under Statutory Norms & Penalty Review»'}
          </div>
          <div className="p-2 bg-blue-950/40 border border-blue-500/30 rounded text-[10px] text-blue-200 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-blue-400 shrink-0" />
            <span>{isDe ? 'Ausgewählt: ' : isPl ? 'Wybrano: ' : isUk ? 'Підібрано флагман: ' : 'Selected Flagship: '}<strong>{isDe ? 'Rechtsgutachter für Wirtschaftsverträge (BGB/HGB)' : isPl ? 'Prawny Audytor Umów Handlowych' : isUk ? 'Юридичний Аудитор Договорів ЦКУ' : 'Commercial Contract Legal Auditor'}</strong></span>
          </div>
        </div>
      )
    },
    {
      stepNumber: 2,
      badge: isUk ? "КРОК 2: ВВЕДЕННЯ ДАНИХ" : isPl ? "KROK 2: WPROWADZENIE DANYCH" : isDe ? "SCHRITT 2: DATENEINGABE" : isFr ? "ÉTAPE 2: SAISIE DES DONNÉES" : "STEP 2: DATA INPUT",
      title: isUk 
        ? "Вставте текст або прикріпіть робочий файл" 
        : isPl 
        ? "Wklej treść lub dołącz plik roboczy" 
        : isDe
        ? "Text einfügen oder Arbeitsdatei hochladen"
        : isFr
        ? "Collez votre texte ou importez votre fichier"
        : "Paste text or upload your working file",
      desc: isUk
        ? "Підтримуються PDF, Word .docx, Excel .xlsx, XML фактур, скан-копії, а також готові тестові пресети в 1 клік."
        : isPl
        ? "Obsługa plików PDF, Word .docx, Excel .xlsx, XML faktur KSeF, skanów oraz gotowych zestawów testowych w 1 kliknięcie."
        : isDe
        ? "Unterstützt PDF, Word .docx, Excel .xlsx, XRechnung XML, Scans sowie fertige Test-Presets per 1-Klick."
        : isFr
        ? "Prend en charge PDF, Word .docx, Excel .xlsx, XML Factur-X, scans OCR et jeux de démo en 1 clic."
        : "Supports PDF, Word (.docx), Excel (.xlsx), e-invoice XML, scan OCR, or 1-click test datasets.",
      icon: Layers,
      mockVisual: (
        <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-2 font-mono text-xs text-left">
          <div className="flex items-center justify-between text-[10px] text-slate-400 border-b border-slate-800 pb-1.5">
            <span>{isDe ? '📎 Hochgeladenes Dokument:' : isPl ? '📎 Wczytany dokument:' : isUk ? '📎 Завантажений документ:' : '📎 Uploaded Document:'}</span>
            <span className="text-blue-400">PDF / 4.2 MB</span>
          </div>
          <div className="p-2 bg-slate-900 rounded border border-slate-700 flex items-center justify-between text-[11px] text-slate-200">
            <span>📄 {isDe ? 'Gewerbemietvertrag_2026_Entwurf.pdf' : isPl ? 'Umowa_Najmu_Lokalu_2026.pdf' : isUk ? 'Dogovir_Orendy_Comertsiyna_2026.pdf' : 'Commercial_Lease_Agreement_2026.pdf'}</span>
            <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 rounded text-[9px] font-bold">{isDe ? '14 S. verarbeitet' : isPl ? '14 str. przetworzono' : isUk ? 'Оброблено 14 ст.' : '14 pages processed'}</span>
          </div>
          <div className="p-2 bg-amber-950/30 border border-amber-500/30 rounded text-[10px] text-amber-200 flex items-center gap-1.5">
            <Cpu className="w-3.5 h-3.5 text-amber-400 shrink-0 animate-spin" />
            <span>{isDe ? 'Tiefenprüfung Gemini 3.7 Flash: 48 Rechtsklauseln analysiert...' : isPl ? 'Głęboka analiza Gemini 3.7 Flash: 48 klauzul prawnych...' : isUk ? 'Глибокий аналіз Gemini 3.7 Flash: перевірка 48 правових пунктів...' : 'Gemini 3.7 Flash deep analysis: 48 legal clauses inspected...'}</span>
          </div>
        </div>
      )
    },
    {
      stepNumber: 3,
      badge: isUk ? "КРОК 3: ФІНАЛЬНИЙ РЕЗУЛЬТАТ" : isPl ? "KROK 3: FINALNY REZULTAT" : isDe ? "SCHRITT 3: OFFIZIELLER BERICHT" : isFr ? "ÉTAPE 3: RAPPORT OFFICIEL" : "STEP 3: OFFICIAL ARTIFACT",
      title: isUk 
        ? "Отримайте висновок та завантажте Word або Excel за 15 секунд" 
        : isPl 
        ? "Pobierz oficjalny raport Word (.docx) lub Excel (.xlsx) w 15 sekund" 
        : isDe
        ? "Gutachten erhalten & Word/Excel-Dateien in 15 Sekunden herunterladen"
        : isFr
        ? "Obtenez l'avis formel et téléchargez Word ou Excel en 15 secondes"
        : "Get compliance statement and download Word or Excel in 15s",
      desc: isUk
        ? "Офіційна юридична шапка, таблиця виправлень, точні формули Excel, відсутність «води» та 100% комплаєнс із чинним законодавством."
        : isPl
        ? "Oficjalna metryka prawna, zestawienie ryzyk, aktywne formuły Excel, brak ogólników i 100% zgodności z przepisami."
        : isDe
        ? "Offizielle rechtliche Kopfzeile, Risikomatrix, aktive Excel-Formeln, null Fülltext und 100% Gesetzeskonformität."
        : isFr
        ? "En-tête juridique officiel, matrice des risques, formules Excel actives, zéro superflu et conformité réglementaire 100%."
        : "Official jurisdictional headers, risk matrices, active Excel formulas, zero fluff, and 100% statutory compliance.",
      icon: ShieldCheck,
      mockVisual: (
        <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-2 font-mono text-xs text-left">
          <div className="flex items-center justify-between text-[10px] text-emerald-400 font-bold border-b border-slate-800 pb-1.5">
            <span>{isDe ? '✅ OFFIZIELLER PRÜFBERICHT ERSTELLT' : isPl ? '✅ OFICJALNY RAPORT SFORMOWANY' : isUk ? '✅ ОФІЦІЙНИЙ ВИСНОВОК СФОРМОВАНО' : '✅ FORMAL COMPLIANCE STATEMENT GENERATED'}</span>
            <span>{isDe ? 'Dauer: 12.4 Sek.' : isPl ? 'Czas: 12.4 sek' : isUk ? 'Час: 12.4 сек' : 'Time: 12.4 sec'}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div className="p-2 bg-blue-900/30 border border-blue-500/40 rounded flex items-center justify-between text-blue-200 font-bold">
              <span>📄 {isDe ? 'Gutachten_Vertrag.docx' : isPl ? 'Raport_Umowa.docx' : isUk ? 'Звіт_Договір.docx' : 'Report_Contract.docx'}</span>
              <Download className="w-3.5 h-3.5 text-blue-400" />
            </div>
            <div className="p-2 bg-emerald-900/30 border border-emerald-500/40 rounded flex items-center justify-between text-emerald-200 font-bold">
              <span>📊 {isDe ? 'Kalkulation_DIN276.xlsx' : isPl ? 'Kosztorys_KNR.xlsx' : isUk ? 'Розрахунок_Кошторис.xlsx' : 'BOQ_Estimate.xlsx'}</span>
              <Download className="w-3.5 h-3.5 text-emerald-400" />
            </div>
          </div>
          <div className="p-2 bg-emerald-950/40 border border-emerald-500/30 rounded text-[10px] text-emerald-300 font-bold flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            <span>{isDe ? 'Sofort einsatzbereit für Mandanten, Behörden und Geschäftsführung!' : isPl ? 'Gotowe do przekazania zarządowi, do urzędu lub sądu!' : isUk ? 'Готово до подачі в суд, ДПС або передачі замовнику!' : 'Ready for board presentation, tax submission, or court filing!'}</span>
          </div>
        </div>
      )
    }
  ];

  const current = steps[currentStep];
  const IconComponent = current.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl max-w-xl w-full p-6 sm:p-7 shadow-2xl space-y-6 relative text-left">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="space-y-1 pr-8">
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-indigo-500/20 text-indigo-300 rounded-lg border border-indigo-400/30">
              <Play className="w-4 h-4 fill-current" />
            </span>
            <span className="text-[11px] font-extrabold text-indigo-300 font-mono uppercase tracking-wider">
              {isUk ? '30-СЕКУНДНИЙ ДЕМО-ТУР' : isPl ? '30-SEKUNDOWY DEMO-TOUR' : isDe ? '30-SEKUNDEN DEMO-TOUR' : isFr ? 'VISITE DÉMO 30 SECONDES' : '30-SECOND QUICK TOUR'}
            </span>
          </div>
          <h2 className="text-lg font-black text-white uppercase tracking-tight">
            {isUk 
              ? 'Як працює Deep Research Hub за 3 прості кроки:' 
              : isPl 
              ? 'Jak działa Deep Research Hub w 3 prostych krokach:' 
              : isDe
              ? 'So funktioniert Deep Research Hub in 3 einfachen Schritten:'
              : isFr
              ? 'Comment fonctionne Deep Research Hub en 3 étapes simples :'
              : 'How Deep Research Hub works in 3 easy steps:'}
          </h2>
        </div>

        {/* Progress Bar */}
        <div className="flex items-center gap-2">
          {steps.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 rounded-full flex-1 transition-all ${
                idx === currentStep 
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-500' 
                  : idx < currentStep 
                  ? 'bg-emerald-500' 
                  : 'bg-slate-800'
              }`}
            />
          ))}
        </div>

        {/* Active Step Content */}
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="inline-block px-2.5 py-1 bg-indigo-950/80 border border-indigo-500/40 rounded-lg text-[10px] font-mono font-extrabold text-indigo-300 uppercase">
              {current.badge}
            </div>
            <h3 className="text-sm sm:text-base font-extrabold text-white">
              {current.title}
            </h3>
            <p className="text-xs text-slate-300 font-mono leading-relaxed">
              {current.desc}
            </p>
          </div>

          {/* Interactive Mock Visual */}
          {current.mockVisual}
        </div>

        {/* Navigation & CTA */}
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-800">
          <button
            type="button"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:pointer-events-none text-slate-300 text-xs font-mono font-bold rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>{isUk ? 'Назад' : isPl ? 'Wstecz' : 'Back'}</span>
          </button>

          {currentStep < steps.length - 1 ? (
            <button
              type="button"
              onClick={() => setCurrentStep(currentStep + 1)}
              className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer"
            >
              <span>{isUk ? 'Далі' : isPl ? 'Dalej' : 'Next Step'}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <a
              href="#login-card-section"
              onClick={onClose}
              className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg flex items-center gap-2 cursor-pointer animate-pulse"
            >
              <Sparkles className="w-4 h-4 text-amber-300" />
              <span>{isUk ? '🚀 Спробувати 1-й запуск безкоштовно' : isPl ? '🚀 Uruchom 1. raz za darmo' : '🚀 Try 1st Run For Free'}</span>
            </a>
          )}
        </div>

      </div>
    </div>
  );
}
